/**
 * SettingsPage - User settings + kiosk device management
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui';
import { useAuthStore, usePhoneStore } from '../stores';
import { supabase } from '../lib/supabase';

interface KioskDevice {
  id: string;
  device_token: string;
  name: string;
  registered_at: string;
  registered_by: string | null;
  is_active: boolean;
  last_seen_at: string | null;
}

async function kioskApiFetch(url: string, options: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers as Record<string, string>,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return response.json();
}

export function SettingsPage() {
  const { user, logout } = useAuthStore();
  const microphone = usePhoneStore((s) => s.microphone);
  const speaker = usePhoneStore((s) => s.speaker);
  const ringtoneVolume = usePhoneStore((s) => s.ringtoneVolume);

  // Kiosk device management state
  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [generatedCode, setGeneratedCode] = useState<{ code: string; expires_at: string } | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      const data = await kioskApiFetch('/api/workforce/kiosk-devices');
      setDevices(data);
    } catch {
      // Ignore — devices table may not exist yet
    } finally {
      setIsLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleGenerateCode = useCallback(async () => {
    if (!newDeviceName.trim()) return;
    setIsGenerating(true);
    setError(null);
    try {
      const data = await kioskApiFetch('/api/workforce/kiosk-devices?action=generate-code', {
        method: 'POST',
        body: JSON.stringify({ device_name: newDeviceName.trim() }),
      });
      setGeneratedCode(data);
      setNewDeviceName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fout bij genereren');
    } finally {
      setIsGenerating(false);
    }
  }, [newDeviceName]);

  const handleToggleDevice = useCallback(async (id: string, isActive: boolean) => {
    try {
      await kioskApiFetch(`/api/workforce/kiosk-devices?id=${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !isActive }),
      });
      await fetchDevices();
    } catch {
      // Ignore
    }
  }, [fetchDevices]);

  const handleDeleteDevice = useCallback(async (id: string) => {
    try {
      await kioskApiFetch(`/api/workforce/kiosk-devices?id=${id}`, {
        method: 'DELETE',
      });
      await fetchDevices();
    } catch {
      // Ignore
    }
  }, [fetchDevices]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getTimeRemaining = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Verlopen';
    const mins = Math.ceil(diff / 60000);
    return `${mins} min geldig`;
  };

  return (
    <>
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-semibold text-main mb-6">
          Instellingen
        </h1>

        {/* Account info */}
        <Card variant="elevated" padding="none" className="mb-6">
          <CardHeader className="px-6 pt-6">
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted">Naam</p>
                <p className="text-main">{user?.displayName || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted">E-mail</p>
                <p className="text-main">{user?.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted">Gebruikersnaam</p>
                <p className="text-main">{user?.username || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Telefoon */}
        <Card variant="elevated" padding="none" className="mb-6">
          <CardHeader className="px-6 pt-6">
            <CardTitle>Telefoon</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <div className="space-y-3 mb-4">
              <div>
                <p className="text-sm text-muted">Microfoon</p>
                <p className="text-main text-sm">
                  {microphone.label || 'Standaard (browser-selectie)'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted">Speaker</p>
                <p className="text-main text-sm">
                  {speaker.label || 'Standaard (browser-selectie)'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted">Beltoon volume</p>
                <p className="text-main text-sm">{Math.round(ringtoneVolume * 100)}%</p>
              </div>
            </div>
            <Link
              to="/telefoon?tab=settings"
              className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Telefoon instellingen beheren →
            </Link>
          </CardContent>
        </Card>

        {/* Kiosk Devices */}
        <Card variant="elevated" padding="none" className="mb-6">
          <CardHeader className="px-6 pt-6">
            <CardTitle>Kiosk Apparaten</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <p className="text-sm text-muted mb-4">
              Alleen geregistreerde apparaten kunnen de prikklok gebruiken.
            </p>

            {/* Device list */}
            {isLoadingDevices ? (
              <div className="flex items-center gap-2 text-sm text-muted py-4">
                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                Laden...
              </div>
            ) : devices.length > 0 ? (
              <div className="space-y-3 mb-6">
                {devices.map(device => (
                  <div key={device.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-main">{device.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          device.is_active
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {device.is_active ? 'Actief' : 'Inactief'}
                        </span>
                      </div>
                      <p className="text-xs text-muted mt-1">
                        Geregistreerd: {formatDate(device.registered_at)}
                        {device.last_seen_at && ` · Laatst gezien: ${formatDate(device.last_seen_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleDevice(device.id, device.is_active)}
                        className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 transition-colors"
                      >
                        {device.is_active ? 'Deactiveer' : 'Activeer'}
                      </button>
                      {!device.is_active && (
                        <button
                          onClick={() => handleDeleteDevice(device.id)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                        >
                          Verwijder
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted py-4 mb-4">
                Nog geen apparaten geregistreerd.
              </p>
            )}

            {/* Generated code display */}
            {generatedCode && (
              <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-sm text-indigo-700 mb-2">Registratiecode:</p>
                <p className="text-3xl font-mono font-bold text-indigo-800 tracking-[0.3em] text-center py-2">
                  {generatedCode.code}
                </p>
                <p className="text-xs text-indigo-500 text-center mt-2">
                  {getTimeRemaining(generatedCode.expires_at)}
                </p>
                <button
                  onClick={() => setGeneratedCode(null)}
                  className="text-xs text-indigo-600 hover:text-indigo-800 mt-2"
                >
                  Sluiten
                </button>
              </div>
            )}

            {/* Register new device */}
            {!generatedCode && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDeviceName}
                  onChange={e => setNewDeviceName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGenerateCode()}
                  placeholder="Naam apparaat (bijv. Kantoor Tablet)"
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
                <Button
                  onClick={handleGenerateCode}
                  disabled={!newDeviceName.trim() || isGenerating}
                >
                  {isGenerating ? 'Genereren...' : 'Genereer code'}
                </Button>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-500 mt-2">{error}</p>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Card variant="elevated" padding="none">
          <CardHeader className="px-6 pt-6">
            <CardTitle>Acties</CardTitle>
          </CardHeader>
          <CardContent className="px-6 pb-6">
            <Button variant="danger" onClick={logout}>
              Uitloggen
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
